import { LitElement, html } from 'lit';
import { property, queryAssignedElements } from 'lit/decorators.js';
import { literal } from 'lit/static-html.js';
import { C11nPrefix, C11nPrefixLiteral } from 'virtual:c11n-prefix';
import slotStyles from './checkbox-group.slot.scss?inline';
import { appendStylesheet, c11nElement } from '../core';

@c11nElement()
export class CheckboxGroupComponent extends LitElement {
  static selector = `${C11nPrefix}-checkbox-group`;
  static literal = literal`${C11nPrefixLiteral}-checkbox-group`;

  @property({ attribute: 'error' , type: Boolean })
  _error?: boolean;

  @property({ attribute: 'hide-legend', type: Boolean })
  hideLegend?: boolean;

  @queryAssignedElements({ slot: 'fieldset', selector: 'fieldset' })
  fieldsetEls!: Array<HTMLFieldSetElement>;

  @queryAssignedElements({ slot: 'hint-error', selector: '[c11n-web-hint-error]' })
  hintErrorEls!: Array<HTMLElement>;

  @property({ attribute: 'c11n-web-checkbox-group', type: Boolean, reflect: true })
  readonly _c11n_web_checkbox_group: boolean = true;

  get fieldsetEl(): HTMLFieldSetElement {
    return this.fieldsetEls && this.fieldsetEls[0];
  }

  get checkboxEls(): Array<HTMLInputElement> {
    return this.fieldsetEl && Array.from(this.fieldsetEl.querySelectorAll('input'));
  }

  get hintErrorEl(): HTMLElement {
    return this.hintErrorEls && this.hintErrorEls[0];
  } 

  get fieldsetId(): string | undefined {
    return this.fieldsetEl?.id; 
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this.appendStyles();
    
    setTimeout(() => {
      this.updateHintErrorError();
      this.updateSlotAttributes();
    });
  }

  override updated(changedProperties: Map<any, any>): void {
    if (changedProperties.has('_error')) {
      this.updateHintErrorError();
    }
    // if (changedProperties.has('indeterminate')) {
    //   // handle indeterminate
    // }
  }

  updateHintErrorError(): void {
    if (this._error) {
      this.hintErrorEl?.setAttribute('error', '');
    } else {
      this.hintErrorEl?.removeAttribute('error');
    }
  }

  updateSlotAttributes(): void {
    if (this.fieldsetId && this.hintErrorEl) {
      this.hintErrorEl.setAttribute('id', `${this.fieldsetId}-description`);
      this.fieldsetEl.setAttribute('aria-describedby', `${this.fieldsetId}-description`);
      
      this.checkboxEls.forEach((inputEl: HTMLInputElement) => {
        inputEl.setAttribute('aria-describedby', `${this.fieldsetId}-description`);
      });
    }
  }

  checkAll(): void {
    if (this.checkboxEls && this.checkboxEls.length) {
      this.checkboxEls.forEach((checkbox: HTMLInputElement) => {
        checkbox.checked = true;
      });
    }
  }

  uncheckAll(): void {
    if (this.checkboxEls && this.checkboxEls.length) {
      this.checkboxEls.forEach((checkbox: HTMLInputElement) => {
        checkbox.checked = false;
      });
    }
  }
  
  appendStyles() {
    const slotStylesheet: string = slotStyles.toString()
      .replaceAll(/\bc11n-web-checkbox-group\b/g, this.tagName.toLowerCase());
    appendStylesheet({ 
      target: this as Node, 
      styles: slotStylesheet, 
      name: `${this.tagName.toLowerCase()}-slot`
    });
  }

  override render() {
    return html`
    <slot name="fieldset"></slot>
    <slot name="hint-error" @slotchange="${this.updateSlotAttributes}"></slot>
    `;
  }
}
export const C11nCheckboxGroup = CheckboxGroupComponent.selector;
export const C11nCheckboxGroupLiteral = CheckboxGroupComponent.literal;

/* An example of the Lit ifDefined directive, in case it turns out to be useful:

class MyElement extends LitElement {
  const maybeTitle = this.shouldShowTitle ? 'Hello, world!' : undefined;

  render() {
    return html`
      <div title="${ifDefined(maybeTitle)}">...</div>
    `;
  }
}

In this example, the title attr of the div element will only be rendered if 
this.shouldShowTitle is true.
*/